import {
    Alert as StreamAlert,
    ArrowLottie,
    CameraStream,
    CameraStreamContext,
    CheckInResponse,
    ClickToCenter,
    ClickToCenterPositioning,
    ControlService,
    DimensionsOptions,
    FullScreen,
    PanTilt,
    PanTiltVal,
    StartStreamResponse,
    StreamBanner,
    StreamingStatus,
    Zoom,
    ZoomInLottie,
    ZoomOutLottie,
    ZoomVal,
    cameraStreamServiceImpl,
    controlServiceImpl,
} from "@lvt/streaming";
import { unwrap } from "@lvt/web-data";
import { Grid } from "@material-ui/core";
import { Alert, Skeleton } from "@material-ui/lab";
import clsx from "clsx";
import Lottie, { LottieRef, LottieRefCurrentProps } from "lottie-react";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useRef, useState, MutableRefObject } from "react";

import { requestQuickVideo } from "@/api/cameras";
import client from "@/api/client";
import { getHeater, getLights } from "@/api/streamMetadata";
import { RootState, useSelector } from "@/store";
import { OpenButton } from "@/utilities/types/streaming";
import { CenterAnimation } from "@/utilities/types/streaming";

import LightsButton from "../ActionButtons/LightsButton/LightsButton";
import PresetsButton from "../ActionButtons/PresetsButton/PresetsButton";
import QuickDownloadButton from "../ActionButtons/QuickDownloadButton/QuickDownloadButton";
import { QuickSettingsButton } from "../ActionButtons/QuickSettingsButton/QuickSettingsButton";
import SnapshotButton from "../ActionButtons/SnapshotButton/SnapshotButton";
import SpeakerButtons from "../ActionButtons/SpeakerButtons/SpeakerButtons";
import SuspendPatrolButton from "../ActionButtons/SuspendPatrolButton/SuspendPatrolButton";
import { StreamCamNav } from "../StreamCamNav/StreamCamNav";
import { generateUpdateCameraAndButton } from "./StreamContent.Utils";
import useStyles from "./StreamContent.styles";
import StreamContentHead from "./StreamContentHead";


interface StreamContentProps {
    liveUnitId?: number;
    cameraId: number;
    allowControlPresets: boolean;
    allowPanTilt: boolean;
    allowClickToCenter: boolean;
    allowZoom: boolean;
    allowHeater: boolean;
    allowLights: boolean;
    allowSpeakerTalkdown: boolean;
    allowQuickSounds: boolean;
    allowSuspendPatol: boolean;
    allowSnapshot: boolean;
    allowSelfService: boolean;
    clientLogo?: string;
}

const StreamContent: React.FC<StreamContentProps> = ({
    liveUnitId,
    cameraId,
    allowControlPresets,
    allowPanTilt,
    allowClickToCenter,
    allowZoom,
    allowHeater,
    allowLights,
    allowSpeakerTalkdown,
    allowQuickSounds,
    allowSuspendPatol,
    allowSnapshot,
    allowSelfService,
    clientLogo,
}) => {
    const meta = useSelector((state) => unwrap(state.stream.streamMeta));
    const videoPreference = useSelector((state) => (state.user.videoPreference == "html5" ? "html5" : "webrtc"));
    const cameraNavList = useSelector((state: RootState) => unwrap(state.cameras.cameraList));
    const divRef = useRef(null);
    const refObserver = useRef<ResizeObserver>();
    const centerAnimationRef = useRef<LottieRef>(null) as MutableRefObject<LottieRefCurrentProps | null>;
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const [dimensions, setDimensions] = useState<DimensionsOptions>();
    const [buttonState, setButtonState] = useState<OpenButton>(OpenButton.None);
    const [cameraList, setCameraList] = useState<{ id: number; name: string }[]>([]);
    const [startStreamData, setStartStreamData] = useState<StartStreamResponse>();
    const [checkInData, setCheckInData] = useState<CheckInResponse>();
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
    const [streamingStatus, setStreamingStatus] = useState<StreamingStatus>(StreamingStatus.Initial);
    const [controlService, setControlService] = useState<ControlService>(controlServiceImpl(client, cameraId!)); // cameraId should never be null
    const [showCenterAnimation, setShowCenterAnimation] = useState<CenterAnimation>();

    const homePreset = meta?.presets.find((p) => p.isHome) ?? meta?.presets[0];
    const cameraStreamService = cameraStreamServiceImpl(client);

    const [currentCameraId, setCurrentCameraId] = useState(cameraId);

    // Set the button state to None i.e. no button is open as we toggle between cameras.
    useEffect(generateUpdateCameraAndButton(cameraId, currentCameraId, setCurrentCameraId, setButtonState), [
        cameraId,
        currentCameraId,
    ]);

    /////////////////////////////////////////////////////////////////////////////////////////////////
    //////////////////////////////////// Action handlers ////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////////
    const startStream = (cameraId: number) => {
        return cameraStreamService.startStream(cameraId).then((data) => setStartStreamData(data));
    };

    const checkIn = (cameraId: number, streamRecordId: number) => {
        return cameraStreamService
            .checkIn(cameraId, streamRecordId)
            .then((checkInResponse) => {
                setCheckInData(checkInResponse);
            })
            .catch((error) => {
                onAlertMessage({
                    type: "checkInFailed",
                    level: "error",
                    message: "Check-in failed",
                });

                throw error;
            });
    };

    const handleQuickVideo = async (cameraIds: number[]) => {
        try {
            await requestQuickVideo(cameraIds);

            closeSnackbar();

            const alert = (
                <Alert key="quick-video-success" severity="success">
                    Your video was requested and will be emailed to you shortly
                </Alert>
            );

            enqueueSnackbar(alert, {
                anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "right",
                },
                autoHideDuration: 8000,
                key: "quick-video-success",
                preventDuplicate: true,
                variant: "success",
            });
        } catch (e) {
            closeSnackbar();

            const alert = (
                <Alert key="quick-video-error" severity="error">
                    An error occurred while requesting your video. Please try again.
                </Alert>
            );

            enqueueSnackbar(alert, {
                anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "right",
                },
                autoHideDuration: 8000,
                key: "quick-video-error",
                preventDuplicate: true,
                variant: "error",
            });
        }
    };

    const handleMenu = (button: OpenButton) => {
        button === buttonState ? setButtonState(OpenButton.None) : setButtonState(button);
    };

    /////////////////////////////////////////////////////////////////////////////////////////////////
    //////////////////////////////////// Event handlers /////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////////
    const onAlertMessage = ({ type, level, message }: StreamAlert) => {
        closeSnackbar(type);

        const AlertNotification = (
            <Alert key={type} severity={level}>
                {message}
            </Alert>
        );

        enqueueSnackbar(AlertNotification, {
            anchorOrigin: {
                vertical: "bottom",
                horizontal: "right",
            },
            autoHideDuration: 8000,
            key: message,
            preventDuplicate: true,
            variant: level,
        });
    };

    const onError = (e: any) => {
        console.log("Stream error", e);
    };

    const onStatusChange = (status: StreamingStatus) => {
        // If this check is not present, the onIdle wake up will use stale streaming data
        if (status === StreamingStatus.Idle) setStartStreamData(undefined);

        setStreamingStatus(status);
    };

    const onFullScreenChange = (isFullScreen: boolean) => {
        setIsFullScreen(isFullScreen);
    };

    const onClickHome = () => {
        if (!homePreset) return;

        setShowCenterAnimation(CenterAnimation.Home);

        if (centerAnimationRef.current) centerAnimationRef.current.goToAndPlay(0);

        controlService.goToPreset(homePreset.id);
    };

    const onClickPanTilt = (direction: PanTiltVal) => {
        switch (direction) {
            case PanTiltVal.Up:
                setShowCenterAnimation(CenterAnimation.Up);
                break;
            case PanTiltVal.Down:
                setShowCenterAnimation(CenterAnimation.Down);
                break;
            case PanTiltVal.Left:
                setShowCenterAnimation(CenterAnimation.Left);
                break;
            case PanTiltVal.Right:
                setShowCenterAnimation(CenterAnimation.Right);
                break;
        }

        if (centerAnimationRef.current) centerAnimationRef.current.goToAndPlay(0);

        controlService.panTilt(direction);
    };

    const onClickZoom = (direction: ZoomVal) => {
        switch (direction) {
            case ZoomVal.In:
                setShowCenterAnimation(CenterAnimation.ZoomIn);
                break;
            case ZoomVal.Out:
                setShowCenterAnimation(CenterAnimation.ZoomOut);
                break;
        }

        if (centerAnimationRef.current) centerAnimationRef.current.goToAndPlay(0);

        controlService.zoom(direction);
    };

    /////////////////////////////////////////////////////////////////////////////////////////////////
    ////////////////////////////////////// React hooks //////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////////
    const refStreamContainerCb = useCallback((node: HTMLDivElement | null) => {
        if (refObserver.current) {
            refObserver.current.disconnect();
        }

        if (node) {
            const resize = () => {
                const streamBox = node?.getBoundingClientRect();

                setDimensions({ width: streamBox?.width, height: streamBox?.height });
            };

            refObserver.current = new ResizeObserver(resize);

            refObserver.current.observe(node);
            resize();
        }
    }, []);

    useEffect(() => {
        if (cameraNavList && cameraId) {
            setCameraList(
                cameraNavList
                    .filter((camera) => meta?.otherCameraIds.includes(camera.id))
                    .map((camera) => ({ id: camera.id, name: camera.cameraName })),
            );
        }
    }, [cameraNavList, cameraId, meta]);

    useEffect(() => {
        if (cameraId) {
            setControlService(controlServiceImpl(client, cameraId));
            setStreamingStatus(StreamingStatus.Initial);
            setStartStreamData(undefined);
        }
    }, [cameraId]);

    /////////////////////////////////////////////////////////////////////////////////////////////////
    //////////////////////////////////// Helper functions ///////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////////
    const getCenterAnimation = () => {
        switch (showCenterAnimation) {
            case CenterAnimation.Up:
                return (
                    <Lottie
                        lottieRef={centerAnimationRef}
                        animationData={ArrowLottie}
                        loop={false}
                        onComplete={() => setShowCenterAnimation(undefined)}
                        className={clsx(classes.centerAnimation, classes.upArrow)}
                    />
                );

            case CenterAnimation.Down:
                return (
                    <Lottie
                        lottieRef={centerAnimationRef}
                        animationData={ArrowLottie}
                        loop={false}
                        onComplete={() => setShowCenterAnimation(undefined)}
                        className={clsx(classes.centerAnimation, classes.downArrow)}
                    />
                );

            case CenterAnimation.Left:
                return (
                    <Lottie
                        lottieRef={centerAnimationRef}
                        animationData={ArrowLottie}
                        loop={false}
                        onComplete={() => setShowCenterAnimation(undefined)}
                        className={clsx(classes.centerAnimation, classes.leftArrow)}
                    />
                );

            case CenterAnimation.Right:
                return (
                    <Lottie
                        lottieRef={centerAnimationRef}
                        animationData={ArrowLottie}
                        loop={false}
                        onComplete={() => setShowCenterAnimation(undefined)}
                        className={clsx(classes.centerAnimation, classes.rightArrow)}
                    />
                );

            case CenterAnimation.ZoomIn:
                return (
                    <Lottie
                        lottieRef={centerAnimationRef}
                        animationData={ZoomInLottie}
                        loop={false}
                        onComplete={() => setShowCenterAnimation(undefined)}
                        className={classes.centerAnimation}
                    />
                );

            case CenterAnimation.ZoomOut:
                return (
                    <Lottie
                        lottieRef={centerAnimationRef}
                        animationData={ZoomOutLottie}
                        loop={false}
                        onComplete={() => setShowCenterAnimation(undefined)}
                        className={classes.centerAnimation}
                    />
                );

            default:
                return null;
        }
    };

    const classes = useStyles();

    return (
        <Grid data-testid="stream-page_stream-content" container wrap="nowrap" className={classes.root}>
            <Grid container direction="column" item xs={9} className={classes.streamSectionWrapper}>
                <Grid item>
                    <StreamContentHead checkInData={checkInData} />
                </Grid>
                <Grid item xs ref={refStreamContainerCb} className={classes.streamContainer}>
                    {cameraId && meta && controlService ? (
                        <CameraStream
                            cameraId={cameraId}
                            canWebRtc={meta.capabilities.webrtc}
                            startStream={startStream}
                            startStreamData={startStreamData ?? null}
                            videoPreference={videoPreference}
                            onAlertMessage={onAlertMessage}
                            onError={onError}
                            dimensionsOptions={dimensions}
                            onStatusChange={onStatusChange}
                            checkIn={checkIn}
                            checkInData={checkInData}
                            checkOut={cameraStreamService.checkOut}
                            inFullScreen={isFullScreen}
                        >
                            <CameraStreamContext.Consumer>
                                {(ctx) => (
                                    <>
                                        <StreamBanner
                                            streamingStatus={streamingStatus}
                                            getTimeUntilIdle={ctx.getTimeUntilIdle}
                                        />
                                        <div className={classes.fadeControls}>
                                            {clientLogo && (
                                                <img
                                                    className={classes.clientLogo}
                                                    src={clientLogo}
                                                    alt="client logo"
                                                />
                                            )}

                                            {showCenterAnimation != undefined && getCenterAnimation()}

                                            {streamingStatus === StreamingStatus.Active && (
                                                <>
                                                    <div className={classes.ptz}>
                                                        {allowPanTilt && (
                                                            <PanTilt
                                                                onClickHome={onClickHome}
                                                                onClickPanTilt={onClickPanTilt}
                                                                className={classes.panTilt}
                                                            />
                                                        )}
                                                        {allowZoom && <Zoom onClickZoom={onClickZoom} />}
                                                    </div>
                                                    <FullScreen
                                                        fullScreenRef={ctx.containerRef}
                                                        onFullScreenChange={onFullScreenChange}
                                                        className={classes.fullScreen}
                                                    />
                                                    {allowClickToCenter && (
                                                        <ClickToCenter
                                                            onClickToCenter={(positioning: ClickToCenterPositioning) =>
                                                                controlService.clickToCenter(positioning)
                                                            }
                                                            dimensions={ctx.videoDimensions}
                                                        />
                                                    )}
                                                </>
                                            )}

                                            <Grid
                                                container
                                                className={classes.actionButtons}
                                                justifyContent="flex-end"
                                                alignItems="center"
                                                ref={divRef}
                                            >
                                                {streamingStatus === StreamingStatus.Active &&
                                                    (allowSpeakerTalkdown || allowQuickSounds) && (
                                                        <SpeakerButtons
                                                            allowSpeakerTalkdown={allowSpeakerTalkdown}
                                                            liveUnitId={meta.liveUnitId}
                                                            menuAnchor={divRef.current}
                                                            handleMenu={handleMenu}
                                                            open={buttonState === OpenButton.SpeakerTalkDown}
                                                        />
                                                    )}
                                                {streamingStatus === StreamingStatus.Active && cameraId && (
                                                    <QuickDownloadButton
                                                        currentCamera={{
                                                            id: cameraId,
                                                            name: meta?.cameraName || "Current",
                                                        }}
                                                        otherCameras={cameraList}
                                                        onSubmit={handleQuickVideo}
                                                        menuAnchor={divRef.current}
                                                        handleMenu={handleMenu}
                                                        open={buttonState === OpenButton.QuickDownload}
                                                    />
                                                )}
                                                {streamingStatus === StreamingStatus.Active && allowSuspendPatol && (
                                                    <SuspendPatrolButton />
                                                )}
                                                {streamingStatus === StreamingStatus.Active && allowSnapshot && (
                                                    <SnapshotButton />
                                                )}
                                                {streamingStatus === StreamingStatus.Active &&
                                                    allowControlPresets &&
                                                    liveUnitId && (
                                                        <PresetsButton
                                                            liveUnitId={liveUnitId}
                                                            allowSelfService={allowSelfService}
                                                            menuAnchor={divRef.current}
                                                            handleMenu={handleMenu}
                                                            open={buttonState === OpenButton.Presets}
                                                        />
                                                    )}
                                                {streamingStatus === StreamingStatus.Active && allowLights && (
                                                    <LightsButton
                                                        lights={getLights(meta.capabilities.relayActions)}
                                                        menuAnchor={divRef.current}
                                                        handleMenu={handleMenu}
                                                        open={buttonState === OpenButton.Lights}
                                                    />
                                                )}
                                                {(allowSelfService || allowHeater) && (
                                                    <QuickSettingsButton
                                                        liveUnitId={meta.liveUnitId}
                                                        allowHeater={allowHeater}
                                                        allowSelfService={allowSelfService}
                                                        canConfigure={startStreamData?.canConfigure}
                                                        heater={getHeater(meta.capabilities.relayActions)}
                                                        menuAnchor={divRef.current}
                                                        handleMenu={handleMenu}
                                                        open={buttonState === OpenButton.QuickSettings}
                                                    />
                                                )}
                                            </Grid>
                                        </div>
                                    </>
                                )}
                            </CameraStreamContext.Consumer>
                        </CameraStream>
                    ) : (
                        <Skeleton height="100%" width="100%" variant="rect" />
                    )}
                </Grid>
            </Grid>
            <Grid item xs={3} className={classes.cameraNavWrapper}>
                <StreamCamNav />
            </Grid>
        </Grid>
    );
};

export default StreamContent;
